import React from 'react';
import { Link } from 'react-router-dom';
import Tooltip from './Tooltip';

/**
 * AssociationBadges Component
 * 
 * Displays visual badges for entity associations with navigation links.
 * Used in tasks, notes, and other components to show related entities.
 * 
 * @param {Object} associations - Association object containing entity IDs and names
 * @param {boolean} showEmpty - Show empty state if no associations (default: false)
 * @param {string} size - Badge size: 'sm' | 'md' | 'lg' (default: 'md')
 */
const AssociationBadges = ({ associations, showEmpty = false, size = 'md' }) => {
  if (!associations) {
    return showEmpty ? (
      <div className="text-sm text-gray-400 italic">No associations</div>
    ) : null;
  }

  const badges = [];

  // Customer badge
  if (associations.customerId && associations.customerName) {
    badges.push({
      type: 'customer',
      id: associations.customerId,
      name: associations.customerName,
      icon: '🏢',
      color: 'gray',
      route: `/customer/${associations.customerId}`
    });
  }

  // Opportunity badge
  if (associations.opportunityId && associations.opportunityNumberLabel) {
    const oppName = associations.description 
      ? `${associations.opportunityNumberLabel}: ${associations.description.substring(0, 30)}` 
      : associations.opportunityNumberLabel;
    badges.push({
      type: 'opportunity',
      id: associations.opportunityId,
      name: oppName,
      icon: 'opportunity-svg',
      color: 'cyan',
      route: `/opportunity/${associations.opportunityId}`
    });
  }

  // Order badge
  if (associations.orderId && associations.orderNumber) {
    const orderName = associations.customerName 
      ? `Order ${associations.orderNumber} - ${associations.customerName}` 
      : `Order ${associations.orderNumber}`;
    badges.push({
      type: 'order',
      id: associations.orderId,
      name: orderName,
      icon: '🛒',
      color: 'blue',
      route: `/order/${associations.orderId}`
    });
  }

  // Contact badge
  if (associations.contactId && associations.contactName) {
    badges.push({
      type: 'contact',
      id: associations.contactId,
      name: associations.contactName,
      icon: '👤',
      color: 'purple',
      route: `/contact/${associations.contactId}`
    });
  }

  // Task badge (for notes associated with tasks)
  if (associations.taskId && associations.taskNumberLabel) {
    badges.push({
      type: 'task',
      id: associations.taskId,
      name: associations.taskNumberLabel,
      icon: 'task-svg',
      color: 'emerald',
      route: `/task/${associations.taskId}`
    });
  }

  if (badges.length === 0) {
    return showEmpty ? (
      <div className="text-sm text-gray-400 italic">No associations</div>
    ) : null;
  }

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const colorClasses = {
    gray: 'bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200',
    green: 'bg-green-100 text-green-800 hover:bg-green-200 border-green-200',
    cyan: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200',
    blue: 'bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200',
    purple: 'bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-200',
    yellow: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-yellow-200',
    emerald: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-emerald-200'
  };

  // Render icon - use SVG for opportunity, task, and order, emoji for others
  const renderIcon = (badge) => {
    if (badge.icon === 'opportunity-svg') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
          <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" fill="#1dc6faff" stroke="#038cb6ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    if (badge.icon === 'task-svg') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
          <rect x="5" y="4" width="14" height="16" rx="2" stroke="#10B981" strokeWidth="1.5" fill="#D1FAE5"/>
          <path d="M8 9l2 2 4-4" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="9" y1="14" x2="16" y2="14" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    return <span>{badge.icon}</span>;
  };

  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((badge) => (
        <Tooltip key={badge.id} text={`View ${badge.type}: ${badge.name}`}>
          <Link
            to={badge.route}
            className={`inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors ${sizeClasses[size]} ${colorClasses[badge.color]}`}
          >
            {renderIcon(badge)}
            <span className="max-w-xs truncate">{badge.name}</span>
          </Link>
        </Tooltip>
      ))}
    </div>
  );
};

/**
 * Single Association Badge (for custom usage)
 */
export const AssociationBadge = ({ type, id, name, icon, size = 'md', onClick }) => {
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const colorClasses = {
    customer: 'bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200',
    opportunity: 'bg-green-100 text-green-800 hover:bg-green-200 border-green-200',
    order: 'bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200',
    contact: 'bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-200',
    task: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-yellow-200'
  };

  const defaultIcons = {
    customer: '🏢',
    opportunity: '💼',
    order: '📦',
    contact: '👤',
    task: '✅'
  };

  const badgeIcon = icon || defaultIcons[type] || '📝';
  const badgeClass = colorClasses[type] || colorClasses.customer;

  return (
    <span
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors ${onClick ? 'cursor-pointer' : ''} ${sizeClasses[size]} ${badgeClass}`}
    >
      <span>{badgeIcon}</span>
      <span className="max-w-xs truncate">{name}</span>
    </span>
  );
};

export default AssociationBadges;




